import DOMPurify from "dompurify";

import { DEBUG } from "./debug";

/**
 * Sanitiza nome de arquivo para uso em Storage (Supabase/S3).
 * Evita "Invalid key" por espaços, acentos e caracteres especiais.
 */
export function sanitizeStorageFileName(fileName: string): string {
  const base = fileName.replace(/\.[^.]+$/, ""); // sem extensão
  const ext = fileName.match(/\.[^.]+$/)?.[0] || "";
  const normalized = base
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // remove acentos (combining marks)
    .replace(/\s+/g, "-") // espaços → hífen
    .replace(/[^a-zA-Z0-9._-]/g, "_"); // resto → underscore
  return (normalized || "file") + ext.toLowerCase();
}

/**
 * Remove máscaras (pontos, traços, parênteses, etc.) de campos que devem ser
 * armazenados como dígitos puros no banco. Usado antes de insert/update
 * em financial_clients, financial_company_groups, external_captadors, etc.
 */
export function stripToDigits(value: string | null | undefined): string | null {
  if (value == null || typeof value !== "string") return null;
  const digits = value.replace(/\D/g, "");
  return digits || null;
}

/** Campos de financial_clients que armazenam apenas dígitos (CPF, CNPJ, CEP, telefone) */
const FINANCIAL_CLIENT_DIGIT_KEYS = [
  "cpf", "cnpj", "responsavel_cpf", "cep", "telefone", "telefone_empresa",
];

/** Campos de financial_company_groups que armazenam apenas dígitos */
const COMPANY_GROUP_DIGIT_KEYS = [
  "controladora_cnpj", "controladora_cpf", "controladora_telefone", "controladora_cep",
  "outra_empresa_cnpj", "outra_empresa_cpf", "outra_empresa_telefone", "outra_empresa_cep",
];

/**
 * Remove máscaras dos campos especificados em um payload.
 * Valores vazios após strip são convertidos em null.
 */
export function stripDigitsInPayload<T extends Record<string, any>>(
  payload: T,
  keys: string[]
): T {
  const out = { ...payload };
  for (const k of keys) {
    if (k in out && out[k] != null && typeof out[k] === "string") {
      const digits = (out[k] as string).replace(/\D/g, "");
      (out as Record<string, unknown>)[k] = digits || null;
    }
  }
  return out;
}

/** Converte string "true"/"false" em boolean. Evita erro de tipo no Supabase. */
function ensureBoolean(value: unknown): boolean {
  if (typeof value === "boolean") return value;
  if (value === "true" || value === 1) return true;
  return false;
}

/** Garante que valores numéricos sejam number, não string. */
function ensureNumber(value: unknown, fallback = 0): number {
  if (typeof value === "number" && !Number.isNaN(value)) return value;
  if (typeof value === "string") {
    const n = parseFloat(value.replace(",", "."));
    return Number.isNaN(n) ? fallback : n;
  }
  return fallback;
}

/** UUID válido (formato básico). */
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Converte "" ou string inválida em null para colunas UUID. Evita "invalid input syntax for type uuid". */
function ensureUuidOrNull(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  return UUID_REGEX.test(trimmed) ? trimmed : null;
}

/** Campos UUID em financial_clients que rejeitam string vazia. */
const FINANCIAL_CLIENT_UUID_KEYS = [
  "captador_id",
  "anfitriao_id",
  "external_captador_id",
  "financial_group_id",
  "fee_category_id",
  "internal_referrer_id",
  "created_by",
];

/** Sanitiza payload de financial_clients antes de enviar ao Supabase.
 * - Remove máscaras de CPF, CNPJ, CEP, telefone.
 * - Converte has_external_captador para boolean.
 * - Garante tax_rate, monthly_fee, external_captador_value, etc. como number.
 * - Converte "" em null para colunas UUID (evita "invalid input syntax for type uuid").
 * - Filtra captador_ids removendo strings vazias.
 */
export function sanitizeFinancialClientPayload<T extends Record<string, any>>(payload: T): T {
  DEBUG("sanitize", "sanitizeFinancialClientPayload input", { keys: Object.keys(payload) });
  const out = stripDigitsInPayload(payload, FINANCIAL_CLIENT_DIGIT_KEYS) as Record<string, unknown>;
  if ("has_external_captador" in out) {
    out.has_external_captador = ensureBoolean(out.has_external_captador);
  }
  const numKeys = ["tax_rate", "monthly_fee", "total_contract_value", "installment_count", "external_captador_value"];
  for (const k of numKeys) {
    if (k in out && out[k] != null && typeof out[k] !== "number") {
      out[k] = ensureNumber(out[k], k === "tax_rate" ? 0.08 : k === "installment_count" ? 1 : 0);
    }
  }
  for (const k of FINANCIAL_CLIENT_UUID_KEYS) {
    if (k in out) {
      const v = ensureUuidOrNull(out[k]);
      if (v === null && out[k] !== undefined) (out as Record<string, unknown>)[k] = null;
      else if (v !== null) (out as Record<string, unknown>)[k] = v;
    }
  }
  if ("captador_ids" in out && Array.isArray(out.captador_ids)) {
    const filtered = (out.captador_ids as string[]).filter((id) => id && typeof id === "string" && UUID_REGEX.test(id.trim()));
    (out as Record<string, unknown>).captador_ids = filtered.length > 0 ? filtered : null;
  }
  return out as T;
}

/** Sanitiza payload de financial_company_groups antes de enviar ao Supabase. */
export function sanitizeCompanyGroupPayload<T extends Record<string, any>>(payload: T): T {
  return stripDigitsInPayload(payload, COMPANY_GROUP_DIGIT_KEYS);
}

/** Campos de external_captadors que armazenam apenas dígitos */
const EXTERNAL_CAPTADOR_DIGIT_KEYS = ["cpf", "banco_titular_cpf", "banco_titular_telefone"];

/** Sanitiza payload de external_captadors antes de enviar ao Supabase. */
export function sanitizeExternalCaptadorPayload<T extends Record<string, any>>(payload: T): T {
  return stripDigitsInPayload(payload, EXTERNAL_CAPTADOR_DIGIT_KEYS);
}

/** Campos de financial_suppliers que armazenam apenas dígitos */
const SUPPLIER_DIGIT_KEYS = ["cpf", "cnpj", "responsavel_cpf", "telefone", "cep"];

/** Campos UUID em financial_suppliers */
const SUPPLIER_UUID_KEYS = ["category_id", "created_by"];

/** Sanitiza payload de financial_suppliers antes de enviar ao Supabase. */
export function sanitizeSupplierPayload<T extends Record<string, any>>(payload: T): T {
  const out = stripDigitsInPayload(payload, SUPPLIER_DIGIT_KEYS) as Record<string, unknown>;
  const numKeys = ["monthly_amount", "installment_count", "total_value"];
  for (const k of numKeys) {
    if (k in out && out[k] != null && typeof out[k] !== "number") {
      out[k] = ensureNumber(out[k], k === "installment_count" ? 1 : 0);
    }
  }
  for (const k of SUPPLIER_UUID_KEYS) {
    if (k in out && out[k] != null) {
      (out as Record<string, unknown>)[k] = ensureUuidOrNull(out[k]);
    }
  }
  return out as T;
}

/** Campos monetários e UUID em financial_transactions */
const TRANSACTION_AMOUNT_KEYS = ["amount"];
const TRANSACTION_UUID_KEYS = ["category_id", "supplier_id", "professional_id", "recurring_expense_id", "created_by"];

/** Sanitiza payload de financial_transactions antes de enviar ao Supabase. */
export function sanitizeTransactionPayload<T extends Record<string, any>>(payload: T): T {
  const out = { ...payload } as Record<string, unknown>;
  for (const k of TRANSACTION_AMOUNT_KEYS) {
    if (k in out && out[k] != null) {
      out[k] = ensureNumber(out[k], 0);
    }
  }
  for (const k of TRANSACTION_UUID_KEYS) {
    if (k in out && out[k] != null) {
      (out as Record<string, unknown>)[k] = ensureUuidOrNull(out[k]);
    }
  }
  return out as T;
}

/** Campos monetários e UUID em financial_monthly_entries */
const MONTHLY_ENTRY_NUM_KEYS = ["bruto", "tributos", "pos_desconto"];
const MONTHLY_ENTRY_UUID_KEYS = ["client_id", "contract_id", "created_by", "approved_by"];

const MONTHLY_ENTRY_INVALID_COLUMNS = [
  "client_name", "payment_mode", "installment_count", "payment_date", "first_due_date",
];

/** Sanitiza payload de financial_monthly_entries antes de enviar ao Supabase. */
export function sanitizeMonthlyEntryPayload<T extends Record<string, any>>(payload: T): T {
  const out = { ...payload } as Record<string, unknown>;
  for (const k of MONTHLY_ENTRY_INVALID_COLUMNS) {
    delete out[k];
  }
  for (const k of MONTHLY_ENTRY_NUM_KEYS) {
    if (k in out && out[k] != null && typeof out[k] !== "number") {
      out[k] = ensureNumber(out[k], 0);
    }
  }
  for (const k of MONTHLY_ENTRY_UUID_KEYS) {
    if (k in out && out[k] != null) {
      (out as Record<string, unknown>)[k] = ensureUuidOrNull(out[k]);
    }
  }
  return out as T;
}

/** Campos de audit_companies que armazenam apenas dígitos */
const AUDIT_COMPANY_DIGIT_KEYS = ["cnpj"];

/** Sanitiza payload de audit_companies antes de enviar ao Supabase. */
export function sanitizeAuditCompanyPayload<T extends Record<string, any>>(payload: T): T {
  return stripDigitsInPayload(payload, AUDIT_COMPANY_DIGIT_KEYS);
}

/** Campos de profiles (usuários) que armazenam apenas dígitos */
const PROFILE_DIGIT_KEYS = ["cpf", "telefone", "cep"];

/** Campos UUID em profiles (user_id opcional em alguns fluxos) */
const PROFILE_UUID_KEYS = ["user_id"];

/**
 * Sanitiza payload de profile (usuário) antes de enviar ao Edge Function ou Supabase.
 * - Remove máscaras de CPF, CEP, Telefone.
 * - Converte strings vazias inválidas em null para UUIDs.
 */
export function sanitizeProfilePayload<T extends Record<string, any>>(payload: T): T {
  const out = stripDigitsInPayload(payload, PROFILE_DIGIT_KEYS) as Record<string, unknown>;
  for (const k of PROFILE_UUID_KEYS) {
    if (k in out && out[k] != null) {
      const v = ensureUuidOrNull(out[k]);
      (out as Record<string, unknown>)[k] = v;
    }
  }
  return out as T;
}

/** Campos de client_portal_profiles que armazenam apenas dígitos */
const CLIENT_PORTAL_PROFILE_DIGIT_KEYS = ["celular", "cep", "cnpj"];

/** Sanitiza payload de client_portal_profiles antes de enviar ao Supabase. */
export function sanitizeClientPortalProfilePayload<T extends Record<string, any>>(payload: T): T {
  return stripDigitsInPayload(payload, CLIENT_PORTAL_PROFILE_DIGIT_KEYS);
}

/** Campos de labor_audit_employees que armazenam apenas dígitos */
const LABOR_EMPLOYEE_DIGIT_KEYS = ["cpf"];

/** Sanitiza payload de labor_audit_employees antes de enviar ao Supabase. */
export function sanitizeLaborEmployeePayload<T extends Record<string, any>>(payload: T): T {
  return stripDigitsInPayload(payload, LABOR_EMPLOYEE_DIGIT_KEYS);
}

const ALLOWED_TAGS = [
  "p", "strong", "em", "u", "span", "ul", "ol", "li", "br",
  "table", "tr", "td", "th", "thead", "tbody",
  "h1", "h2", "h3", "h4", "h5", "h6",
  "sub", "sup", "blockquote", "img",
];

const ALLOWED_ATTR = ["style", "class", "src", "alt", "width", "height"];

export function sanitizeHtml(html: string): string {
  const out = DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
  });
  DEBUG("sanitize", "sanitizeHtml", { inputLen: html.length, outputLen: out.length });
  return out;
}
